import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BarrierDto, SensorDto } from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

type BarrierForm = {
  parkingId: string;
  code: string;
  name: string;
  mode: 'AUTOMATIC' | 'MANUAL';
  apiEndpoint: string;
};

type SensorForm = {
  parkingId: string;
  targetType: 'SPOT' | 'BARRIER';
  spotId: string;
  barrierId: string;
  apiEndpoint: string;
  externalId: string;
  batteryPercent: string;
  isOccupied: boolean;
};

const emptyBarrierForm = (): BarrierForm => ({
  parkingId: '',
  code: '',
  name: '',
  mode: 'AUTOMATIC',
  apiEndpoint: '',
});

const emptySensorForm = (): SensorForm => ({
  parkingId: '',
  targetType: 'SPOT',
  spotId: '',
  barrierId: '',
  apiEndpoint: '',
  externalId: '',
  batteryPercent: '',
  isOccupied: false,
});

export function BarriersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('parkings:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('parkings:write'));
  const canWriteSensors = useAuthStore(
    (s) => s.hasPermission('spots:write') || s.hasPermission('parkings:write'),
  );

  const parkings = useQuery({ queryKey: ['parkings'], queryFn: () => api.listParkings() });
  const list = useQuery({
    queryKey: ['barriers'],
    queryFn: () => api.listBarriers(),
    enabled: canRead,
  });
  const sensors = useQuery({
    queryKey: ['sensors'],
    queryFn: () => api.listSensors(),
    enabled: canRead,
  });

  const [barrierOpen, setBarrierOpen] = useState(false);
  const [editingBarrierId, setEditingBarrierId] = useState<string | null>(null);
  const [barrierForm, setBarrierForm] = useState<BarrierForm>(emptyBarrierForm);
  const [barrierError, setBarrierError] = useState<string | null>(null);

  const [sensorOpen, setSensorOpen] = useState(false);
  const [editingSensorId, setEditingSensorId] = useState<string | null>(null);
  const [sensorForm, setSensorForm] = useState<SensorForm>(emptySensorForm);
  const [sensorError, setSensorError] = useState<string | null>(null);

  const spots = useQuery({
    queryKey: ['spots'],
    queryFn: () => api.listSpots(),
    enabled: canRead,
  });

  const saveBarrier = useMutation({
    mutationFn: async () => {
      const payload = {
        parkingId: barrierForm.parkingId,
        code: barrierForm.code.trim(),
        name: barrierForm.name.trim(),
        mode: barrierForm.mode,
        apiEndpoint: barrierForm.apiEndpoint.trim() || null,
      };
      if (editingBarrierId) {
        return api.updateBarrier(editingBarrierId, payload);
      }
      return api.createBarrier(payload);
    },
    onSuccess: async () => {
      setBarrierOpen(false);
      setEditingBarrierId(null);
      setBarrierError(null);
      await qc.invalidateQueries({ queryKey: ['barriers'] });
    },
    onError: (e: Error) => setBarrierError(e.message),
  });

  const deleteBarrier = useMutation({
    mutationFn: (id: string) => api.deleteBarrier(id),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['barriers'] }),
  });

  const cmd = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'open' | 'close' | 'status' }) => {
      if (action === 'open') return api.openBarrier(id);
      if (action === 'close') return api.closeBarrier(id);
      return api.barrierStatus(id);
    },
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['barriers'] }),
  });

  const saveSensor = useMutation({
    mutationFn: async () => {
      const battery =
        sensorForm.batteryPercent.trim() === ''
          ? null
          : Number.parseInt(sensorForm.batteryPercent, 10);
      const payload = {
        parkingId: sensorForm.parkingId,
        targetType: sensorForm.targetType,
        spotId: sensorForm.targetType === 'SPOT' ? sensorForm.spotId || null : null,
        barrierId: sensorForm.targetType === 'BARRIER' ? sensorForm.barrierId || null : null,
        apiEndpoint:
          sensorForm.targetType === 'BARRIER'
            ? sensorForm.apiEndpoint.trim() || null
            : null,
        externalId: sensorForm.externalId.trim(),
        batteryPercent: Number.isFinite(battery as number) ? battery : null,
        isOccupied: sensorForm.isOccupied,
      };
      if (editingSensorId) {
        return api.updateSensor(editingSensorId, payload);
      }
      return api.createSensor(payload);
    },
    onSuccess: async () => {
      setSensorOpen(false);
      setEditingSensorId(null);
      setSensorError(null);
      await qc.invalidateQueries({ queryKey: ['sensors'] });
      await qc.invalidateQueries({ queryKey: ['spots'] });
      await qc.invalidateQueries({ queryKey: ['barriers'] });
    },
    onError: (e: Error) => setSensorError(e.message),
  });

  const deleteSensor = useMutation({
    mutationFn: (id: string) => api.deleteSensor(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sensors'] });
      await qc.invalidateQueries({ queryKey: ['spots'] });
    },
  });

  const parkingNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of parkings.data?.data ?? []) {
      map.set(p.id, p.name);
    }
    return map;
  }, [parkings.data?.data]);

  const spotCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of spots.data?.data ?? []) {
      map.set(s.id, s.code);
    }
    return map;
  }, [spots.data?.data]);

  const barrierLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of list.data?.data ?? []) {
      map.set(b.id, `${b.code} — ${b.name}`);
    }
    return map;
  }, [list.data?.data]);

  const availableSpots = useMemo(() => {
    const rows = (spots.data?.data ?? []).filter((s) => s.parkingId === sensorForm.parkingId);
    const taken = new Set(
      (sensors.data?.data ?? [])
        .filter((s) => s.id !== editingSensorId)
        .map((s) => s.spotId)
        .filter((id): id is string => Boolean(id)),
    );
    return rows.filter((s) => !taken.has(s.id));
  }, [spots.data?.data, sensors.data?.data, editingSensorId, sensorForm.parkingId]);

  const availableBarriers = useMemo(() => {
    return (list.data?.data ?? []).filter((b) => b.parkingId === sensorForm.parkingId);
  }, [list.data?.data, sensorForm.parkingId]);

  function sensorTargetLabel(s: SensorDto): string {
    if (s.targetType === 'BARRIER') {
      return barrierLabelById.get(s.barrierId ?? '') ?? t('barriers.targetBarrier');
    }
    return spotCodeById.get(s.spotId ?? '') ?? t('barriers.targetSpot');
  }

  if (!canRead) {
    return <div className="alert alert-warning">{t('barriers.forbidden')}</div>;
  }

  const barrierRows = list.data?.data ?? [];
  const sensorRows = sensors.data?.data ?? [];
  const barrierColumns = [
    t('barriers.code'),
    t('barriers.name'),
    t('barriers.mode'),
    t('barriers.state'),
    ...(canWrite ? [t('common.actions')] : []),
  ];
  const sensorColumns = [
    t('barriers.externalId'),
    t('barriers.parking'),
    t('barriers.target'),
    t('barriers.targetRef'),
    t('barriers.occupied'),
    t('barriers.battery'),
    t('barriers.lastSeen'),
    ...(canWriteSensors ? [t('common.actions')] : []),
  ];

  function openCreateBarrier() {
    setEditingBarrierId(null);
    setBarrierError(null);
    setBarrierForm({
      ...emptyBarrierForm(),
      parkingId: parkings.data?.data[0]?.id ?? '',
    });
    setBarrierOpen(true);
  }

  function openEditBarrier(b: BarrierDto) {
    setEditingBarrierId(b.id);
    setBarrierError(null);
    setBarrierForm({
      parkingId: b.parkingId,
      code: b.code,
      name: b.name,
      mode: b.mode,
      apiEndpoint: b.apiEndpoint ?? '',
    });
    setBarrierOpen(true);
  }

  function openCreateSensor() {
    setEditingSensorId(null);
    setSensorError(null);
    setSensorForm({
      ...emptySensorForm(),
      parkingId: parkings.data?.data[0]?.id ?? '',
    });
    setSensorOpen(true);
  }

  function openEditSensor(s: SensorDto) {
    setEditingSensorId(s.id);
    setSensorError(null);
    setSensorForm({
      parkingId: s.parkingId,
      targetType: s.targetType,
      spotId: s.spotId ?? '',
      barrierId: s.barrierId ?? '',
      apiEndpoint: s.apiEndpoint ?? '',
      externalId: s.externalId,
      batteryPercent: s.batteryPercent != null ? String(s.batteryPercent) : '',
      isOccupied: s.isOccupied,
    });
    setSensorOpen(true);
  }

  function barrierActionButtons(b: BarrierDto) {
    return (
      <div className="d-flex gap-1 justify-content-center flex-wrap align-items-center">
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => cmd.mutate({ id: b.id, action: 'open' })}
        >
          {t('barriers.open')}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => cmd.mutate({ id: b.id, action: 'close' })}
        >
          {t('barriers.close')}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light"
          onClick={() => cmd.mutate({ id: b.id, action: 'status' })}
        >
          {t('barriers.refresh')}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-icon btn-ghost-primary"
          aria-label={t('barriers.edit')}
          onClick={() => openEditBarrier(b)}
        >
          <i className="bi bi-pencil" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-icon btn-ghost-danger"
          aria-label={t('common.delete')}
          onClick={() => {
            if (window.confirm(t('barriers.confirmDelete'))) {
              deleteBarrier.mutate(b.id);
            }
          }}
        >
          <i className="bi bi-trash" />
        </button>
      </div>
    );
  }

  function sensorActionButtons(s: SensorDto) {
    return (
      <div className="d-flex gap-1 justify-content-center">
        <button
          type="button"
          className="btn btn-sm btn-icon btn-ghost-primary"
          aria-label={t('barriers.editSensor')}
          onClick={() => openEditSensor(s)}
        >
          <i className="bi bi-pencil" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-icon btn-ghost-danger"
          aria-label={t('common.delete')}
          onClick={() => {
            if (window.confirm(t('barriers.confirmDeleteSensor'))) {
              deleteSensor.mutate(s.id);
            }
          }}
        >
          <i className="bi bi-trash" />
        </button>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary" onClick={openCreateBarrier}>
              {t('barriers.create')}
            </button>
          ) : undefined
        }
      />

      <Card className="mb-4">
        <ParkingDataTable
          storageKey="barriers"
          columns={barrierColumns}
          refreshKey={barrierRows.map((b) => `${b.id}:${b.state}:${b.updatedAt}`).join('|')}
          actionsColumn={canWrite}
          galleryItems={barrierRows.map((b) => ({
            id: b.id,
            icon: 'bi-door-open',
            iconTone: statusToneFromEnum(b.state),
            title: b.code,
            subtitle: b.name,
            meta: enumLabel(t, 'barrierMode', b.mode),
            badge: (
              <StatusBadge tone={statusToneFromEnum(b.state)}>
                {enumLabel(t, 'barrierState', b.state)}
              </StatusBadge>
            ),
            actions: canWrite ? barrierActionButtons(b) : undefined,
          }))}
        >
          {barrierRows.map((b) => (
            <tr key={b.id}>
              <td className="fw-semibold">{b.code}</td>
              <td>{b.name}</td>
              <td>{enumLabel(t, 'barrierMode', b.mode)}</td>
              <td>
                <StatusBadge tone={statusToneFromEnum(b.state)}>
                  {enumLabel(t, 'barrierState', b.state)}
                </StatusBadge>
              </td>
              {canWrite && <td>{barrierActionButtons(b)}</td>}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <Card
        title={t('barriers.sensors')}
        toolbar={
          canWriteSensors ? (
            <button type="button" className="btn btn-sm btn-primary" onClick={openCreateSensor}>
              {t('barriers.createSensor')}
            </button>
          ) : undefined
        }
      >
        <ParkingDataTable
          storageKey="barriers-sensors"
          columns={sensorColumns}
          refreshKey={sensorRows.map((s) => `${s.id}:${s.updatedAt}`).join('|')}
          actionsColumn={canWriteSensors}
          galleryItems={sensorRows.map((s) => ({
            id: s.id,
            icon: 'bi-broadcast',
            iconTone: s.isOccupied ? ('warning' as const) : ('success' as const),
            title: s.externalId,
            subtitle: `${parkingNameById.get(s.parkingId) ?? '—'} · ${enumLabel(t, 'sensorTarget', s.targetType)} · ${sensorTargetLabel(s)}`,
            meta: `${s.isOccupied ? t('common.activeOn') : t('common.activeOff')} · ${s.batteryPercent ?? '—'}% · ${s.lastSeenAt ? new Date(s.lastSeenAt).toLocaleString() : '—'}`,
            actions: canWriteSensors ? sensorActionButtons(s) : undefined,
          }))}
        >
          {sensorRows.map((s) => (
            <tr key={s.id}>
              <td className="fw-semibold">{s.externalId}</td>
              <td>{parkingNameById.get(s.parkingId) ?? '—'}</td>
              <td>{enumLabel(t, 'sensorTarget', s.targetType)}</td>
              <td>{sensorTargetLabel(s)}</td>
              <td>{s.isOccupied ? t('common.activeOn') : t('common.activeOff')}</td>
              <td>{s.batteryPercent ?? '—'}%</td>
              <td>{s.lastSeenAt ? new Date(s.lastSeenAt).toLocaleString() : '—'}</td>
              {canWriteSensors && <td>{sensorActionButtons(s)}</td>}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={barrierOpen}
        onClose={() => setBarrierOpen(false)}
        title={editingBarrierId ? t('barriers.edit') : t('barriers.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setBarrierOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                !barrierForm.parkingId ||
                !barrierForm.code.trim() ||
                !barrierForm.name.trim() ||
                saveBarrier.isPending
              }
              onClick={() => saveBarrier.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {barrierError && <div className="alert alert-danger mb-0">{barrierError}</div>}
          <div>
            <label className="form-label">{t('barriers.parking')}</label>
            <select
              className="form-select"
              value={barrierForm.parkingId}
              onChange={(e) => setBarrierForm({ ...barrierForm, parkingId: e.target.value })}
            >
              <option value="">—</option>
              {(parkings.data?.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('barriers.code')}</label>
            <input
              className="form-control"
              value={barrierForm.code}
              onChange={(e) => setBarrierForm({ ...barrierForm, code: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('barriers.name')}</label>
            <input
              className="form-control"
              value={barrierForm.name}
              onChange={(e) => setBarrierForm({ ...barrierForm, name: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('barriers.mode')}</label>
            <select
              className="form-select"
              value={barrierForm.mode}
              onChange={(e) =>
                setBarrierForm({
                  ...barrierForm,
                  mode: e.target.value as BarrierForm['mode'],
                })
              }
            >
              <option value="AUTOMATIC">{enumLabel(t, 'barrierMode', 'AUTOMATIC')}</option>
              <option value="MANUAL">{enumLabel(t, 'barrierMode', 'MANUAL')}</option>
            </select>
          </div>
          <div>
            <label className="form-label">{t('barriers.endpoint')}</label>
            <input
              className="form-control"
              value={barrierForm.apiEndpoint}
              placeholder="https://"
              onChange={(e) => setBarrierForm({ ...barrierForm, apiEndpoint: e.target.value })}
            />
          </div>
        </div>
      </AppModal>

      <AppModal
        open={sensorOpen}
        onClose={() => setSensorOpen(false)}
        title={editingSensorId ? t('barriers.editSensor') : t('barriers.createSensor')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setSensorOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                !sensorForm.parkingId ||
                !sensorForm.externalId.trim() ||
                (sensorForm.targetType === 'SPOT' && !sensorForm.spotId) ||
                (sensorForm.targetType === 'BARRIER' && !sensorForm.barrierId) ||
                saveSensor.isPending
              }
              onClick={() => saveSensor.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {sensorError && <div className="alert alert-danger mb-0">{sensorError}</div>}
          <div>
            <label className="form-label">{t('barriers.parking')}</label>
            <select
              className="form-select"
              value={sensorForm.parkingId}
              onChange={(e) =>
                setSensorForm({
                  ...sensorForm,
                  parkingId: e.target.value,
                  spotId: '',
                  barrierId: '',
                })
              }
            >
              <option value="">—</option>
              {(parkings.data?.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">{t('barriers.target')}</label>
            <select
              className="form-select"
              value={sensorForm.targetType}
              onChange={(e) =>
                setSensorForm({
                  ...sensorForm,
                  targetType: e.target.value as SensorForm['targetType'],
                  spotId: '',
                  barrierId: '',
                  apiEndpoint: '',
                })
              }
            >
              <option value="SPOT">{enumLabel(t, 'sensorTarget', 'SPOT')}</option>
              <option value="BARRIER">{enumLabel(t, 'sensorTarget', 'BARRIER')}</option>
            </select>
            <div className="form-text">{t('barriers.targetHelp')}</div>
          </div>
          {sensorForm.targetType === 'SPOT' ? (
            <div>
              <label className="form-label">{t('barriers.spot')}</label>
              <select
                className="form-select"
                value={sensorForm.spotId}
                disabled={!sensorForm.parkingId || spots.isLoading}
                onChange={(e) => setSensorForm({ ...sensorForm, spotId: e.target.value })}
              >
                <option value="">—</option>
                {availableSpots.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code}
                  </option>
                ))}
              </select>
              <div className="form-text">{t('barriers.spotHelp')}</div>
            </div>
          ) : (
            <>
              <div>
                <label className="form-label">{t('barriers.barrier')}</label>
                <select
                  className="form-select"
                  value={sensorForm.barrierId}
                  disabled={!sensorForm.parkingId}
                  onChange={(e) => setSensorForm({ ...sensorForm, barrierId: e.target.value })}
                >
                  <option value="">—</option>
                  {availableBarriers.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.code} — {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">{t('barriers.sensorEndpoint')}</label>
                <input
                  className="form-control"
                  value={sensorForm.apiEndpoint}
                  placeholder="https://"
                  onChange={(e) => setSensorForm({ ...sensorForm, apiEndpoint: e.target.value })}
                />
                <div className="form-text">{t('barriers.sensorEndpointHelp')}</div>
              </div>
            </>
          )}
          <div>
            <label className="form-label">{t('barriers.externalId')}</label>
            <input
              className="form-control"
              value={sensorForm.externalId}
              onChange={(e) => setSensorForm({ ...sensorForm, externalId: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('barriers.battery')}</label>
            <input
              type="number"
              min={0}
              max={100}
              className="form-control"
              value={sensorForm.batteryPercent}
              onChange={(e) => setSensorForm({ ...sensorForm, batteryPercent: e.target.value })}
            />
          </div>
          <div className="form-check">
            <input
              id="sensor-occupied"
              type="checkbox"
              className="form-check-input"
              checked={sensorForm.isOccupied}
              onChange={(e) => setSensorForm({ ...sensorForm, isOccupied: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="sensor-occupied">
              {t('barriers.occupied')}
            </label>
          </div>
        </div>
      </AppModal>
    </>
  );
}
